/**
 * Domestic Number Detection Utility
 * 
 * Detects local/domestic phone number formats for key countries and converts them to E.164.
 * This allows users to dial numbers without country codes when calling from their home country.
 */

import { DOMESTIC_PATTERNS, getSortedPatterns, type DomesticPattern, type DomesticPatternRule } from "@/config/geo/domestic-patterns";
import { parsePhoneNumberFromString, getCountries, getCountryCallingCode, type CountryCode } from "libphonenumber-js";
import { canonicalizePhone } from "@/lib/phone-canonical";

export interface DomesticDetectionResult {
  countryCode: string;
  countryName: string;
  flag: string;
  type: string;
  description: string;
  normalizedNumber: string; // E.164 format (e.g., +61412345678)
}

/**
 * Detect if a phone number matches a domestic pattern for any supported country.
 * Returns the first matching country with E.164 normalized number.
 * 
 * @param digits - Phone number digits only (no spaces, dashes, or + prefix)
 * @returns Detection result or null if no domestic pattern matches
 */
export function detectDomesticNumber(digits: string): DomesticDetectionResult | null {
  if (!digits || digits.length < 7) return null;

  const sortedPatterns = getSortedPatterns();

  for (const country of sortedPatterns) {
    for (const pattern of country.patterns) {
      if (pattern.regex.test(digits)) {
        const normalizedNumber = convertToE164(digits, country.countryCode, pattern);
        
        return {
          countryCode: country.countryCode,
          countryName: country.countryName,
          flag: country.flag,
          type: pattern.type,
          description: pattern.description,
          normalizedNumber,
        };
      }
    }
  }

  return null;
}

/**
 * Convert a domestic number to E.164 format.
 * 
 * @param digits - Phone number digits only
 * @param countryCode - E.164 country code (e.g., "61" for Australia)
 * @param pattern - The matching pattern rule
 * @returns E.164 formatted number (e.g., +61412345678)
 */
function convertToE164(digits: string, countryCode: string, pattern: DomesticPatternRule): string {
  let nationalNumber = digits;

  // Handle special prefix stripping (for NANP 11-digit numbers)
  if (pattern.stripPrefix !== undefined && pattern.stripPrefix > 0) {
    nationalNumber = digits.slice(pattern.stripPrefix);
  }
  // Handle leading zero stripping (common for AU, NZ, UK)
  else if (pattern.stripLeadingZero && digits.startsWith("0")) {
    nationalNumber = digits.slice(1);
  }

  return `+${countryCode}${nationalNumber}`;
}

/**
 * Normalize a phone number to E.164 format using domestic detection.
 * Falls back to simple country code prefixing if no domestic pattern matches.
 * 
 * @param phoneNumber - Raw phone number input (may include spaces, dashes, etc.)
 * @param countryCode - Default country code to use if no domestic pattern matches
 * @returns E.164 formatted number or null if conversion fails
 */
export function normalizeDomesticToE164(phoneNumber: string, countryCode: string): string | null {
  if (!phoneNumber) return null;

  const raw = phoneNumber.trim();
  const digitsOnly = raw.replace(/\D/g, "");
  const selectedIso2 = getIso2ForCallingCode(countryCode);

  // Canonicalise first: metadata-driven repair of trunk zeros, IDD prefixes and
  // duplicated country codes for every numbering plan (same rules as backend).
  const canonical = canonicalizePhone(raw, selectedIso2 ?? countryCode);
  if (canonical) return canonical.e164;


  // AU mobile shorthand: users type 4XXXXXXXX instead of 04XXXXXXXX.
  if (selectedIso2 === "AU" && /^4\d{8}$/.test(digitsOnly)) {
    const parsed = parsePhoneNumberFromString(`0${digitsOnly}`, "AU");
    if (parsed?.isValid()) return parsed.format("E.164");
  }

  // Already in international format - sanitize embedded trunk zeros
  if (raw.startsWith("+")) {
    const digits = raw.replace(/\D/g, "");
    return `+${stripTrunkZero(digits)}`;
  }

  // Handle international prefixes
  let normalized = raw;
  if (normalized.startsWith("00")) {
    return `+${normalized.slice(2).replace(/\D/g, "")}`;
  }
  if (normalized.startsWith("011")) {
    return `+${normalized.slice(3).replace(/\D/g, "")}`;
  }

  // Extract digits only
  const digits = normalized.replace(/\D/g, "");

  // Try domestic pattern detection
  const domesticResult = detectDomesticNumber(digits);
  if (domesticResult) {
    return domesticResult.normalizedNumber;
  }

  // Fallback: Check if number already starts with country code
  if (digits.startsWith(countryCode) && digits.length > countryCode.length + 6) {
    return `+${digits}`;
  }

  // Fallback: Simple prefix with country code
  // For NANP (country code 1), don't add leading 0 logic
  if (countryCode === "1") {
    // For US/Canada, just prefix with +1
    return `+1${digits}`;
  }

  // For other countries, strip leading zero if present and prefix
  const nationalNumber = digits.startsWith("0") ? digits.slice(1) : digits;
  return `+${countryCode}${nationalNumber}`;
}

function getIso2ForCallingCode(countryCode: string): CountryCode | undefined {
  const raw = String(countryCode || "").trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(raw)) {
    try {
      if (getCountries().includes(raw as CountryCode)) return raw as CountryCode;
    } catch { /* fall through */ }
  }

  const clean = raw.replace(/\D/g, "");
  if (!clean) return undefined;

  // Prefer AU for +61 because it is the caller's primary market and avoids
  // ambiguities with territories that share the same calling code.
  if (clean === "61") return "AU";

  return getCountries().find((iso2) => {
    try {
      return getCountryCallingCode(iso2) === clean;
    } catch {
      return false;
    }
  });
}

/**
 * Check if a number is in a format that looks like it could be domestic
 * (i.e., doesn't start with + or international prefixes)
 * 
 * @param phoneNumber - Raw phone number input
 * @returns true if the number appears to be in domestic format
 */
export function isPotentiallyDomestic(phoneNumber: string): boolean {
  if (!phoneNumber) return false;
  
  const trimmed = phoneNumber.trim();
  
  // Not domestic if starts with + or international prefixes
  if (trimmed.startsWith("+")) return false;
  if (trimmed.startsWith("00")) return false;
  if (trimmed.startsWith("011")) return false;
  
  // Get digits
  const digits = trimmed.replace(/\D/g, "");
  
  // Must have reasonable length
  if (digits.length < 7 || digits.length > 12) return false;
  
  return true;
}

/**
 * Get country info for a specific country code from domestic patterns
 */
export function getDomesticPatternForCountry(countryCode: string): DomesticPattern | undefined {
  return DOMESTIC_PATTERNS.find(p => p.countryCode === countryCode);
}

// Countries that use 0 as domestic trunk prefix
const TRUNK_ZERO_COUNTRIES = [
  '61', '64', '44', '49', '33', '39', '91', '86', '81', '82',
  '63', '62', '66', '84', '60',
];

/**
 * Strip embedded trunk zero from an E.164-style digit string.
 * e.g. "610433799880" → "61433799880"
 * Only strips the zero immediately after a recognized country code.
 */
export function stripTrunkZero(digits: string): string {
  for (const cc of TRUNK_ZERO_COUNTRIES) {
    if (digits.startsWith(cc) && digits.length > cc.length + 5) {
      const national = digits.slice(cc.length);
      if (national.startsWith('0')) {
        return cc + national.slice(1);
      }
    }
  }
  return digits;
}
